import * as React from "react";
import { cn } from "@/lib/cn";

// ════════════════════════════════════════════════════════════════════════
// tktic · Logo system
// ────────────────────────────────────────────────────────────────────────
// Three exports: <Mark> (Tactic-Board clipboard with O · X · cyan dotted
// line), <Wordmark> (lowercase wordmark with play-tittle on dotless 'ı'),
// <Logo> (horizontal lockup combining the two).
//
// All components accept a `size` (pixel height) and `tone` to switch
// between brand-on-light, reverse-on-dark, and current-color modes.
// ════════════════════════════════════════════════════════════════════════

export type LogoTone = "default" | "inverse" | "mono";

type ColorPair = { primary: string; accent: string };

const tonePair = (tone: LogoTone): ColorPair => {
  switch (tone) {
    case "inverse":
      return { primary: "var(--ink-inverse)", accent: "var(--product-accent)" };
    case "mono":
      return { primary: "currentColor", accent: "currentColor" };
    default:
      return { primary: "var(--ink-primary)", accent: "var(--product-accent)" };
  }
};

// ─── Mark ───────────────────────────────────────────────────────────────
export interface MarkProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  tone?: LogoTone;
}

export const Mark = React.forwardRef<SVGSVGElement, MarkProps>(
  ({ size = 32, tone = "default", className, ...rest }, ref) => {
    const { primary, accent } = tonePair(tone);
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox="0 0 96 96"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="tktic"
        className={cn("shrink-0", className)}
        {...rest}
      >
        {/* clipboard board */}
        <rect
          x="18"
          y="22"
          width="60"
          height="62"
          rx="6"
          fill="none"
          stroke={primary}
          strokeWidth={4}
        />
        {/* clip + tab */}
        <rect x="38" y="14" width="20" height="14" rx="3" fill={primary} />
        <rect x="44" y="10" width="8" height="4" rx="1" fill={primary} />
        {/* O */}
        <circle
          cx="36"
          cy="46"
          r="7"
          fill="none"
          stroke={primary}
          strokeWidth={3.5}
        />
        {/* X */}
        <g stroke={primary} strokeWidth={3.5} strokeLinecap="round">
          <line x1="56" y1="62" x2="66" y2="72" />
          <line x1="66" y1="62" x2="56" y2="72" />
        </g>
        {/* single dotted line O → X (always accent / cyan) */}
        <line
          x1="41"
          y1="51"
          x2="56"
          y2="63"
          stroke={accent}
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray="0.1 6"
        />
      </svg>
    );
  },
);
Mark.displayName = "Mark";

// ─── Wordmark ───────────────────────────────────────────────────────────
export interface WordmarkProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: number;
  tone?: LogoTone;
}

export const Wordmark = React.forwardRef<HTMLSpanElement, WordmarkProps>(
  ({ size = 32, tone = "default", className, style, ...rest }, ref) => {
    const { primary, accent } = tonePair(tone);
    return (
      <span
        ref={ref}
        aria-label="tktic"
        className={cn(
          "inline-flex items-baseline font-display font-bold leading-none",
          className,
        )}
        style={{
          fontSize: size,
          letterSpacing: "-0.05em",
          color: primary,
          ...style,
        }}
        {...rest}
      >
        <span aria-hidden="true">tkt</span>
        {/* Dotless ı (U+0131) — leaves the tittle slot empty so the play
            triangle replaces it without colliding with the font's dot. */}
        <span
          style={{ position: "relative", display: "inline-block" }}
          aria-hidden="true"
        >
          <span>ı</span>
          <svg
            width={size * 0.26}
            height={size * 0.26}
            viewBox="0 0 10 10"
            style={{
              position: "absolute",
              left: "50%",
              top: -size * 0.02,
              transform: "translateX(-50%)",
              overflow: "visible",
            }}
          >
            <path
              d="M2.2 1.4 L8.6 5 L2.2 8.6 Z"
              fill={accent}
              stroke={accent}
              strokeWidth={0.4}
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span aria-hidden="true">c</span>
      </span>
    );
  },
);
Wordmark.displayName = "Wordmark";

// ─── Logo (horizontal lockup) ───────────────────────────────────────────
export interface LogoProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Pixel height of the mark + wordmark. */
  size?: number;
  tone?: LogoTone;
  /** Show mark only, wordmark only, or full lockup. */
  variant?: "lockup" | "mark" | "wordmark";
}

export const Logo = React.forwardRef<HTMLDivElement, LogoProps>(
  (
    { size = 32, tone = "default", variant = "lockup", className, ...rest },
    ref,
  ) => {
    if (variant === "mark") {
      return (
        <Mark
          ref={ref as never}
          size={size}
          tone={tone}
          className={className}
          {...(rest as Record<string, unknown>)}
        />
      );
    }
    if (variant === "wordmark") {
      return (
        <Wordmark
          ref={ref as never}
          size={size}
          tone={tone}
          className={className}
          {...(rest as Record<string, unknown>)}
        />
      );
    }
    return (
      <div
        ref={ref}
        aria-label="tktic"
        className={cn("inline-flex items-center", className)}
        style={{ gap: size * 0.2 }}
        {...rest}
      >
        <Mark size={size} tone={tone} aria-hidden="true" />
        <Wordmark size={size} tone={tone} aria-hidden="true" />
      </div>
    );
  },
);
Logo.displayName = "Logo";
